import { ApiError } from "@/src/libs/ApiError";

/**
 * Délai au-delà duquel une requête est abandonnée.
 *
 * Sans lui, un serveur qui accepte la connexion mais ne répond jamais laisse
 * l'écran sur un indicateur de chargement **infini**, sans message ni porte de
 * sortie. C'est un bug déjà vécu côté app.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/** Code métier porté par l'`ApiError` produite en cas de dépassement. */
export const REQUEST_TIMEOUT_CODE = "REQUEST_TIMEOUT";

/**
 * Chemins exemptés de délai.
 *
 * Un envoi de fichier peut légitimement dépasser 30 s ; l'interrompre ferait
 * perdre le transfert. Ajoute ici toute route d'upload.
 */
const TIMEOUT_EXEMPT_PATHS = ["/api/v1/profile/upload-avatar"];

export function isTimeoutExempt(url: string): boolean {
  return TIMEOUT_EXEMPT_PATHS.some((path) => url.includes(path));
}

/** `true` si l'erreur vient d'un dépassement de délai, pas du serveur. */
export function isTimeoutError(error: unknown): boolean {
  return ApiError.from(error)?.code === REQUEST_TIMEOUT_CODE;
}

/**
 * `fetch` borné dans le temps, branché sur les deux clients d'API.
 *
 * Le contrôleur est monté à la main plutôt qu'avec `AbortSignal.any` : c'est ce
 * qui permet de **distinguer** un dépassement de délai d'une annulation amont
 * (React Query au démontage d'un écran) et de ne convertir que le premier en
 * erreur. L'annulation amont, elle, est relayée telle quelle — sans ce relais,
 * remplacer le signal ferait perdre toute annulation.
 *
 * Un dépassement devient une `ApiError` 408 : il emprunte donc les mêmes
 * chemins d'erreur que le reste, au lieu d'un `AbortError` nu que personne ne
 * sait lire.
 */
export async function fetchWithTimeout(
  request: Request,
  /** Seulement pour les tests : la production utilise toujours la valeur par défaut. */
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  if (isTimeoutExempt(request.url)) {
    return fetch(request);
  }

  const controller = new AbortController();
  let hasTimedOut = false;

  const timer = setTimeout(() => {
    hasTimedOut = true;
    controller.abort();
  }, timeoutMs);

  const upstream: AbortSignal | undefined = request.signal;
  const forwardAbort = () => controller.abort();

  if (upstream) {
    if (upstream.aborted) forwardAbort();
    else upstream.addEventListener("abort", forwardAbort);
  }

  try {
    return await fetch(request, { signal: controller.signal });
  } catch (error) {
    if (hasTimedOut) {
      throw new ApiError(408, "Request Timeout", {
        error: REQUEST_TIMEOUT_CODE,
      });
    }
    throw error;
  } finally {
    clearTimeout(timer);
    upstream?.removeEventListener("abort", forwardAbort);
  }
}
